import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Gift, Heart } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface AgeResult {
  years: number;
  months: number;
  days: number;
  totalDays: number;
  totalHours: number;
  totalMinutes: number;
  totalSeconds: number;
  nextBirthday: {
    months: number;
    days: number;
    totalDays: number;
  };
  zodiacSign: string;
  dayOfWeek: string;
}

const AgeCalculator: React.FC = () => {
  const [birthDate, setBirthDate] = useState('');
  const [targetDate, setTargetDate] = useState(new Date().toISOString().split('T')[0]);
  const [ageResult, setAgeResult] = useState<AgeResult | null>(null);
  const [isRealTime, setIsRealTime] = useState(true);

  useSEO({
    title: 'Age Calculator - Calculate Your Exact Age',
    description: 'Free online age calculator. Calculate your exact age in years, months, days, hours, minutes, and seconds. Find your zodiac sign and next birthday.',
    keywords: 'age calculator, calculate age, birthday calculator, age in days, age in hours, zodiac sign calculator',
    canonical: 'https://snap-tools.online/calc/age-calculator'
  });

  const getZodiacSign = (month: number, day: number): string => {
    const signs = [
      { name: 'Capricorn', start: [12, 22], end: [1, 19] },
      { name: 'Aquarius', start: [1, 20], end: [2, 18] },
      { name: 'Pisces', start: [2, 19], end: [3, 20] },
      { name: 'Aries', start: [3, 21], end: [4, 19] },
      { name: 'Taurus', start: [4, 20], end: [5, 20] },
      { name: 'Gemini', start: [5, 21], end: [6, 20] },
      { name: 'Cancer', start: [6, 21], end: [7, 22] },
      { name: 'Leo', start: [7, 23], end: [8, 22] },
      { name: 'Virgo', start: [8, 23], end: [9, 22] },
      { name: 'Libra', start: [9, 23], end: [10, 22] },
      { name: 'Scorpio', start: [10, 23], end: [11, 21] },
      { name: 'Sagittarius', start: [11, 22], end: [12, 21] }
    ];

    for (const sign of signs) {
      if (sign.name === 'Capricorn') {
        if ((month === sign.start[0] && day >= sign.start[1]) || (month === sign.end[0] && day <= sign.end[1])) {
          return sign.name;
        }
      } else if ((month === sign.start[0] && day >= sign.start[1]) || (month === sign.end[0] && day <= sign.end[1])) {
        return sign.name;
      }
    }
    return 'Unknown';
  };

  const calculateAge = (birth: Date, target: Date): AgeResult => {
    const birthTime = birth.getTime();
    const targetTime = target.getTime();
    
    if (birthTime > targetTime) {
      throw new Error('Birth date cannot be in the future');
    }

    // Calculate years, months, days
    let years = target.getFullYear() - birth.getFullYear();
    let months = target.getMonth() - birth.getMonth();
    let days = target.getDate() - birth.getDate();

    if (days < 0) {
      months--;
      const lastDayOfPrevMonth = new Date(target.getFullYear(), target.getMonth(), 0).getDate();
      days += lastDayOfPrevMonth;
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    // Calculate totals
    const totalDays = Math.floor((targetTime - birthTime) / (1000 * 60 * 60 * 24));
    const totalHours = Math.floor((targetTime - birthTime) / (1000 * 60 * 60));
    const totalMinutes = Math.floor((targetTime - birthTime) / (1000 * 60));
    const totalSeconds = Math.floor((targetTime - birthTime) / 1000);

    // Calculate next birthday
    const nextBirthdayYear = target.getFullYear() + (target.getMonth() > birth.getMonth() || 
      (target.getMonth() === birth.getMonth() && target.getDate() >= birth.getDate()) ? 1 : 0);
    const nextBirthdayDate = new Date(nextBirthdayYear, birth.getMonth(), birth.getDate());
    const daysToNextBirthday = Math.ceil((nextBirthdayDate.getTime() - targetTime) / (1000 * 60 * 60 * 24));
    
    const nextBirthdayMonths = Math.floor(daysToNextBirthday / 30);
    const nextBirthdayDays = daysToNextBirthday % 30;

    // Get zodiac sign and day of week
    const zodiacSign = getZodiacSign(birth.getMonth() + 1, birth.getDate());
    const dayOfWeek = birth.toLocaleDateString('en-US', { weekday: 'long' });

    return {
      years,
      months,
      days,
      totalDays,
      totalHours,
      totalMinutes,
      totalSeconds,
      nextBirthday: {
        months: nextBirthdayMonths,
        days: nextBirthdayDays,
        totalDays: daysToNextBirthday
      },
      zodiacSign,
      dayOfWeek
    };
  };

  const handleCalculate = () => {
    if (!birthDate) {
      toast.error('Please enter your birth date');
      return;
    }

    try {
      const birth = new Date(birthDate);
      const target = isRealTime ? new Date() : new Date(targetDate);
      
      const result = calculateAge(birth, target);
      setAgeResult(result);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Invalid date');
    }
  };

  useEffect(() => {
    if (birthDate) {
      handleCalculate();
    }
  }, [birthDate, targetDate, isRealTime]);

  // Real-time update effect
  useEffect(() => {
    if (!isRealTime || !birthDate) return;

    const interval = setInterval(() => {
      handleCalculate();
    }, 1000);

    return () => clearInterval(interval);
  }, [isRealTime, birthDate]);

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const ageStats = [
    { label: 'Years', value: ageResult?.years, icon: Calendar, color: 'blue' },
    { label: 'Months', value: ageResult?.months, icon: Calendar, color: 'green' },
    { label: 'Days', value: ageResult?.days, icon: Calendar, color: 'purple' },
    { label: 'Total Days', value: ageResult?.totalDays, icon: Clock, color: 'orange' },
    { label: 'Total Hours', value: ageResult?.totalHours, icon: Clock, color: 'red' },
    { label: 'Total Minutes', value: ageResult?.totalMinutes, icon: Clock, color: 'pink' },
    { label: 'Total Seconds', value: ageResult?.totalSeconds, icon: Clock, color: 'indigo' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Calendar className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            Age Calculator
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Calculate your exact age in years, months, days, hours, minutes, and seconds. 
            Discover your zodiac sign and days until your next birthday.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calculator */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Calculate Your Age</h2>
              
              {/* Birth Date Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Birth Date
                </label>
                <input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-lg"
                />
              </div>

              {/* Real-time toggle */}
              <div className="mb-6">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isRealTime}
                    onChange={(e) => setIsRealTime(e.target.checked)}
                    className="w-4 h-4 text-[#8438ff] border-gray-300 rounded focus:ring-[#8438ff]"
                  />
                  <span className="text-sm text-gray-700">Real-time calculation (updates every second)</span>
                </label>
              </div>

              {/* Target Date Input (when not real-time) */}
              {!isRealTime && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Calculate age as of
                  </label>
                  <input
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-lg"
                  />
                </div>
              )}

              {/* Age Results */}
              {ageResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  {/* Main Age Display */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6 text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      You are {ageResult.years} years, {ageResult.months} months, and {ageResult.days} days old
                    </h3>
                    <p className="text-gray-600">
                      Born on a {ageResult.dayOfWeek} • Zodiac sign: {ageResult.zodiacSign}
                    </p>
                  </div>

                  {/* Age Statistics Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {ageStats.map((stat, index) => (
                      <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className={`bg-${stat.color}-50 border border-${stat.color}-200 rounded-lg p-4 text-center`}
                      >
                        <stat.icon className={`h-6 w-6 text-${stat.color}-600 mx-auto mb-2`} />
                        <div className={`text-2xl font-bold text-${stat.color}-700 mb-1`}>
                          {stat.value ? formatNumber(stat.value) : '0'}
                        </div>
                        <div className="text-xs text-gray-600">{stat.label}</div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Next Birthday */}
            {ageResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Gift className="h-5 w-5 text-[#8438ff]" />
                  <h3 className="font-semibold text-gray-900">Next Birthday</h3>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#8438ff] mb-2">
                    {ageResult.nextBirthday.totalDays}
                  </div>
                  <div className="text-sm text-gray-600 mb-3">
                    days until you turn {ageResult.years + 1}
                  </div>
                  <div className="text-xs text-gray-500">
                    {ageResult.nextBirthday.months} months and {ageResult.nextBirthday.days} days
                  </div>
                </div>
              </motion.div>
            )}

            {/* Fun Facts */}
            {ageResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Heart className="h-5 w-5 text-red-500" />
                  <h3 className="font-semibold text-gray-900">Fun Facts</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Heartbeats:</span>
                    <span className="font-medium">{formatNumber(Math.floor(ageResult.totalSeconds * 1.2))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Breaths taken:</span>
                    <span className="font-medium">{formatNumber(Math.floor(ageResult.totalMinutes * 16))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Weeks lived:</span>
                    <span className="font-medium">{formatNumber(Math.floor(ageResult.totalDays / 7))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Months lived:</span>
                    <span className="font-medium">{formatNumber(ageResult.years * 12 + ageResult.months)}</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Zodiac Info */}
            {ageResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
              >
                <h3 className="font-semibold text-gray-900 mb-3">Your Zodiac Sign</h3>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#8438ff] mb-2">
                    {ageResult.zodiacSign}
                  </div>
                  <div className="text-sm text-gray-600">
                    Based on your birth date
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
};

export default AgeCalculator;
